import React from 'react';
import { Home, Search, PlusSquare, Bell, User, Film } from 'lucide-react';

/**
 * Bottom navigation. Phase 5 polish:
 *  - `aria-label` on the <nav> + `aria-current="page"` on the active tab so
 *    screen readers announce the current location.
 *  - Each button still has visible text + icon, so we don't add a redundant
 *    `aria-label` (would cause double-read). Instead the badge gets a
 *    descriptive `aria-label` and is `aria-hidden` from the icon side.
 *  - Safe-area padding so the bar clears the iOS home-indicator without
 *    cutting off labels.
 *  - `min-h-[56px]` per button ensures the standard 44px touch target.
 *
 * Phase 6 update: 6th tab "Reels" inserted between Explore and Create. Grid
 * widens to ``grid-cols-6``; on 360px viewports the inner labels stay
 * legible because we already use ``text-xs`` + truncate. No new dependency.
 */
const BottomNav = ({ active, onChange, t, isRTL, unreadCount = 0 }) => {
  const items = [
    { key: 'feed', label: t('home'), Icon: Home, testid: 'nav-home' },
    { key: 'explore', label: t('explore'), Icon: Search, testid: 'nav-explore' },
    { key: 'reels', label: t('reels'), Icon: Film, testid: 'nav-reels' },
    { key: 'create', label: t('createPost'), Icon: PlusSquare, testid: 'nav-create' },
    {
      key: 'notifications',
      label: t('notifications'),
      Icon: Bell,
      testid: 'nav-notifications',
      badge: unreadCount,
    },
    { key: 'profile', label: t('profile'), Icon: User, testid: 'nav-profile' },
  ];

  return (
    <nav
      data-testid="bottom-nav"
      dir={isRTL ? 'rtl' : 'ltr'}
      aria-label={t('primaryNav')}
      className="fixed bottom-0 inset-x-0 bg-white border-t border-gray-200 z-40"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <ul className="max-w-2xl mx-auto grid grid-cols-6">
        {items.map(({ key, label, Icon, testid, badge }) => {
          const isActive = active === key;
          return (
            <li key={key}>
              <button
                type="button"
                onClick={() => onChange(key)}
                data-testid={testid}
                aria-current={isActive ? 'page' : undefined}
                className={`relative w-full flex flex-col items-center justify-center gap-1 min-h-[56px] py-2 text-xs transition-colors ${
                  isActive ? 'text-purple-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="relative">
                  <Icon
                    aria-hidden="true"
                    className={`w-6 h-6 ${isActive ? 'stroke-[2.4]' : ''}`}
                  />
                  {badge > 0 && (
                    <span
                      data-testid={`${testid}-badge`}
                      aria-label={`${badge} ${label}`}
                      className="absolute -top-1 -end-2 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none"
                    >
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </span>
                <span className="truncate">{label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default BottomNav;
